import { useFeatureFlagVariantKey } from 'posthog-js/react';

import type { BillingProfileResponse } from 'api/types';
import { useGetBillingProfile } from 'entities/billing/api/useGetBillingProfile';
import { useAuth } from 'shared/lib/auth/useAuth';
import { FeatureFlag, UpgradePromoLayoutVariant } from 'shared/lib/features/common';

import { INDIVIDUAL_REPROMPT_DAYS, SELF_SERVE_USAGE_THRESHOLD_PCT } from './constants';
import { nextTierPitch, type PlanPitch } from './tierPitch';
import { type UsageSignals, useEstimatedUsagePct } from './useEstimatedUsagePct';
import { usePromoStore } from './usePromoStore';

export type UpgradeLayout = 'modal' | 'toast' | 'banner';

export type UpgradeDecision =
  | { eligible: false; reason: 'enterprise' | 'no_permission' | 'top_of_ladder' | 'cooldown' | 'loading' }
  | { eligible: true; pitch: PlanPitch; layout: UpgradeLayout };

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const daysSince = (iso: string | null): number => {
  if (!iso) return Number.POSITIVE_INFINITY;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return Number.POSITIVE_INFINITY;
  return (Date.now() - ts) / MS_PER_DAY;
};

const repromptThreshold = (dismissCount: number): number => {
  if (dismissCount <= 0) return 0;
  return INDIVIDUAL_REPROMPT_DAYS[Math.min(dismissCount - 1, INDIVIDUAL_REPROMPT_DAYS.length - 1)];
};

const isModalOrToast = (variant: unknown): variant is UpgradePromoLayoutVariant =>
  variant === UpgradePromoLayoutVariant.MODAL || variant === UpgradePromoLayoutVariant.TOAST;

type AuthState = ReturnType<typeof useAuth>;
type AuthUser = NonNullable<AuthState['user']>;
type Permissions = AuthState['permissions'];

type AccessInputs = { user: AuthUser | null; permissions: Permissions };

const checkAccess = ({ user, permissions }: AccessInputs): UpgradeDecision | null => {
  if (!user) return { eligible: false, reason: 'loading' };
  if (!(user.isCompanyIndividual || user.isCompanySelfServe)) {
    return { eligible: false, reason: 'enterprise' };
  }
  if (!permissions.has('can_edit_billing')) {
    return { eligible: false, reason: 'no_permission' };
  }
  return null;
};

type PlanInputs = { profile: BillingProfileResponse | undefined; isProfilePending: boolean };

const checkPlan = ({ profile, isProfilePending }: PlanInputs): UpgradeDecision | { pitch: PlanPitch } => {
  if (isProfilePending) return { eligible: false, reason: 'loading' };
  const currentPlan = profile?.subscription_id ?? 'one_off';
  const pitch = nextTierPitch(currentPlan);
  if (!pitch) return { eligible: false, reason: 'top_of_ladder' };
  return { pitch };
};

type CooldownInputs = {
  user: AuthUser;
  lastDismissedAt: string | null;
  dismissCount: number;
  ledger: UsageSignals;
};

const checkCooldown = ({ user, lastDismissedAt, dismissCount, ledger }: CooldownInputs): UpgradeDecision | null => {
  if (user.isCompanyIndividual) {
    if (daysSince(lastDismissedAt) < repromptThreshold(dismissCount)) {
      return { eligible: false, reason: 'cooldown' };
    }
    return null;
  }
  // Self-serve: trigger on usage threshold OR a mid-cycle top-up purchase.
  if (!ledger.isReady) return { eligible: false, reason: 'loading' };
  if (!(ledger.topUpAdded || ledger.usagePct >= SELF_SERVE_USAGE_THRESHOLD_PCT)) {
    return { eligible: false, reason: 'cooldown' };
  }
  return null;
};

type LayoutInputs = {
  demotedToBanner: boolean;
  variantBucket: string | null;
  variantFromFlag: unknown;
};

const resolveLayout = ({ demotedToBanner, variantBucket, variantFromFlag }: LayoutInputs): UpgradeLayout => {
  // Once the user clicks "Maybe later" the banner sticks regardless of the
  // A/B variant — we respect the user's preference over flag purity.
  if (demotedToBanner) return 'banner';
  // Otherwise prefer the bucket recorded for this user, fall back to the
  // current PostHog evaluation, then to `modal` if neither resolved.
  const stickyVariant = isModalOrToast(variantBucket) ? variantBucket : null;
  const flagVariant = isModalOrToast(variantFromFlag) ? variantFromFlag : null;
  return stickyVariant ?? flagVariant ?? UpgradePromoLayoutVariant.MODAL;
};

/**
 * Centralised eligibility selector for the upgrade-plan promo (CL-6252).
 * Returns either a {@link UpgradeDecision} with the pitched tier + chosen
 * layout, or a structured `eligible: false` reason for analytics.
 */
export const useUpgradeTrigger = (): UpgradeDecision => {
  const { user, permissions } = useAuth();
  const { data: profile, isPending: isProfilePending } = useGetBillingProfile();
  // Skip the credit-ledger query unless the user could actually trigger via
  // self-serve usage thresholds — saves a per-page request for individual /
  // enterprise users.
  const ledger = useEstimatedUsagePct({ enabled: !!user?.isCompanySelfServe });
  const { lastDismissedAt, dismissCount, demotedToBanner, variantBucket } = usePromoStore();
  const variantFromFlag = useFeatureFlagVariantKey(FeatureFlag.UPGRADE_PROMO_LAYOUT);

  const accessBlock = checkAccess({ user, permissions });
  if (accessBlock) return accessBlock;

  const planResult = checkPlan({ profile, isProfilePending });
  if ('eligible' in planResult) return planResult;

  // user is non-null past checkAccess.
  const cooldownBlock = checkCooldown({ user: user as AuthUser, lastDismissedAt, dismissCount, ledger });
  if (cooldownBlock) return cooldownBlock;

  const layout = resolveLayout({ demotedToBanner, variantBucket, variantFromFlag });
  return { eligible: true, pitch: planResult.pitch, layout };
};
